/**
 * Multi-Day Event Utilities
 * 
 * These utilities help manage events that span multiple days.
 * Events are stored with startDate and endDate fields, and we compute
 * which dates they appear on dynamically.
 */

/**
 * Check if a date falls within an event's date range
 * @param {string} dateKey - The date to check (YYYY-MM-DD format)
 * @param {Object} event - The event object with potential startDate/endDate
 * @returns {boolean} - True if the date is within the event's range
 */
export const isDateInEventRange = (dateKey, event) => {
  // If not a multi-day event, only match the exact date
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return false;
  }

  return dateKey >= event.startDate && dateKey <= event.endDate;
};

/**
 * Get all date keys that an event spans
 * @param {Object} event - The event object
 * @returns {string[]} - Array of date keys (YYYY-MM-DD format)
 */
export const getEventDateRange = (event) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return [];
  }

  const dates = [];
  
  // Parse dates manually to avoid timezone issues
  const [startYear, startMonth, startDay] = event.startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = event.endDate.split('-').map(Number);
  
  const startDate = new Date(startYear, startMonth - 1, startDay);
  const endDate = new Date(endYear, endMonth - 1, endDay);

  const current = new Date(startDate);
  while (current <= endDate) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }

  return dates;
};

/**
 * Get the position of a date within a multi-day event
 * @param {string} dateKey - The date to check
 * @param {Object} event - The event object
 * @returns {'start' | 'middle' | 'end' | 'single' | null} - Position in the range
 */
export const getDatePositionInEvent = (dateKey, event) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return 'single';
  }

  if (dateKey === event.startDate && dateKey === event.endDate) {
    return 'single';
  }
  if (dateKey === event.startDate) {
    return 'start';
  }
  if (dateKey === event.endDate) {
    return 'end';
  }
  if (dateKey > event.startDate && dateKey < event.endDate) {
    return 'middle';
  }
  return null;
};

/**
 * Calculate the number of days in an event
 * @param {Object} event - The event object
 * @returns {number} - Number of days (1 for single-day events)
 */
export const getEventDayCount = (event) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return 1;
  }

  // Parse dates manually to avoid timezone issues
  const [startYear, startMonth, startDay] = event.startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = event.endDate.split('-').map(Number);
  
  const start = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);
  
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return diffDays;
};

/**
 * Format date range for display
 * @param {Object} event - The event object
 * @returns {string} - Formatted date range string
 */
export const formatEventDateRange = (event) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return '';
  }

  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const startFormatted = formatDate(event.startDate);
  const endFormatted = formatDate(event.endDate);
  const dayCount = getEventDayCount(event);

  return `${startFormatted} - ${endFormatted} (${dayCount} days)`;
};

/**
 * Get shows for a specific date, including multi-day events that span this date
 * This is the main function to use when rendering a calendar day
 * 
 * @param {string} dateKey - The date to get shows for (YYYY-MM-DD format)
 * @param {Object} shows - The shows object keyed by dateKey
 * @returns {Object[]} - Array of shows/events for this date
 */
export const getShowsForDate = (dateKey, shows) => {
  // Get shows stored directly on this date
  const directShows = shows[dateKey] || [];
  
  // Filter direct shows to only non-multi-day OR multi-day events that START on this date
  const singleDayShows = directShows.filter(show => !show.isMultiDay);
  
  // Get the multi-day event stored on this date (if any) - it starts here
  const multiDayStartingHere = directShows.filter(show => 
    show.isMultiDay && show.startDate === dateKey
  ).map(event => ({
    ...event,
    _multiDayPosition: 'start',
    _isMultiDayInstance: true,
    _displayDateKey: dateKey
  }));
  
  // Extract all multi-day events from all dates
  const allMultiDayEvents = extractMultiDayEvents(shows);
  
  // Find multi-day events that span this date (but don't start here)
  const multiDaySpanningHere = allMultiDayEvents.filter(event => {
    // Skip if this is the start date (already handled above)
    if (event.startDate === dateKey) return false;
    // Include if this date is within the event's range
    return isDateInEventRange(dateKey, event);
  }).map(event => ({
    ...event,
    _multiDayPosition: getDatePositionInEvent(dateKey, event),
    _isMultiDayInstance: true,
    _displayDateKey: dateKey
  }));

  return [...singleDayShows, ...multiDayStartingHere, ...multiDaySpanningHere];
};

/**
 * Extract all multi-day events from the shows object
 * @param {Object} shows - The shows object keyed by dateKey
 * @returns {Object[]} - Array of all multi-day events
 */
export const extractMultiDayEvents = (shows) => {
  const multiDayEvents = [];
  const seenIds = new Set();

  Object.values(shows).forEach(dayShows => {
    // Skip if dayShows is not an array
    if (!Array.isArray(dayShows)) return;
    
    dayShows.forEach(show => {
      if (show.isMultiDay && show.startDate && show.endDate && !seenIds.has(show.id)) {
        multiDayEvents.push(show);
        seenIds.add(show.id);
      }
    });
  });

  return multiDayEvents;
};

/**
 * Get a label for display based on position in multi-day event
 * @param {Object} event - The event object with _multiDayPosition
 * @returns {string} - Label like "Day 1 of 3"
 */
export const getMultiDayLabel = (event, dateKey) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return '';
  }

  const totalDays = getEventDayCount(event);
  const dates = getEventDateRange(event);
  const dayNumber = dates.indexOf(dateKey) + 1;

  if (dayNumber === 1) {
    return `Day 1 of ${totalDays}`;
  } else if (dayNumber === totalDays) {
    return `Day ${totalDays} of ${totalDays}`;
  } else {
    return `Day ${dayNumber} of ${totalDays}`;
  }
};

/**
 * Get visual styling classes based on position in multi-day event
 * @param {Object} event - The event object
 * @param {string} dateKey - The current date being displayed
 * @returns {Object} - Object with styling properties
 */
export const getMultiDayStyles = (event, dateKey) => {
  if (!event.isMultiDay || !event.startDate || !event.endDate) {
    return {
      position: 'single',
      containerClass: 'rounded-lg',
      showStartIcon: false,
      showEndIcon: false,
      showConnector: false,
      label: ''
    };
  }

  const position = getDatePositionInEvent(dateKey, event);
  const totalDays = getEventDayCount(event);
  const dates = getEventDateRange(event);
  const dayNumber = dates.indexOf(dateKey) + 1;

  switch (position) {
    case 'start':
      return {
        position: 'start',
        containerClass: 'rounded-l-lg rounded-r-none border-r-0',
        showStartIcon: true,
        showEndIcon: false,
        showConnector: true,
        connectorSide: 'right',
        label: `Day 1 of ${totalDays}`,
        shortLabel: '1→',
        icon: 'start'
      };
    case 'middle':
      return {
        position: 'middle',
        containerClass: 'rounded-none border-x-0',
        showStartIcon: false,
        showEndIcon: false,
        showConnector: true,
        connectorSide: 'both',
        label: `Day ${dayNumber} of ${totalDays}`,
        shortLabel: `${dayNumber}`,
        icon: 'middle'
      };
    case 'end':
      return {
        position: 'end',
        containerClass: 'rounded-r-lg rounded-l-none border-l-0',
        showStartIcon: false,
        showEndIcon: true,
        showConnector: true,
        connectorSide: 'left',
        label: `Day ${totalDays} of ${totalDays}`,
        shortLabel: `→${totalDays}`,
        icon: 'end'
      };
    default:
      return {
        position: 'single',
        containerClass: 'rounded-lg',
        showStartIcon: false,
        showEndIcon: false,
        showConnector: false,
        label: ''
      };
  }
};

/**
 * Get icon component name based on position
 * @param {string} position - 'start', 'middle', 'end', or 'single'
 * @returns {string} - Icon name to use
 */
export const getPositionIcon = (position) => {
  switch (position) {
    case 'start': return 'play'; // ▶ or Play icon
    case 'middle': return 'minus'; // ─ or continuation
    case 'end': return 'flag'; // 🏁 or Flag icon
    default: return null;
  }
};

export default {
  isDateInEventRange,
  getEventDateRange,
  getDatePositionInEvent,
  getEventDayCount,
  formatEventDateRange,
  getShowsForDate,
  extractMultiDayEvents,
  getMultiDayLabel,
  getMultiDayStyles,
  getPositionIcon
};